/**
 * `team` — Settings → Team page wire surface.
 *
 * Procedures:
 *   - `list`              — members + pending invites
 *   - `invite`            — issue an invite (stepUp)
 *   - `resendInvite`      — rotate token + bump counter (stepUp)
 *   - `cancelInvite`      — revoke pending invite (stepUp)
 *   - `changeRole`        — promote/demote (stepUp; last-Founder protected)
 *   - `revokeMembership`  — soft-delete (stepUp; last-Founder protected)
 *   - `lookupInvite`      — public; the /join page reads this to render the
 *                           inviter + workspace + role
 *   - `acceptInvite`      — authed (after OTP); creates the membership
 *   - `listSessions`      — Founder-only (router-enforced)
 *   - `revokeSession`     — Founder-only + stepUp
 *   - `revokeAllSessions` — Founder-only + stepUp ("Sign out everywhere")
 */
import { z } from "zod";
import { phoneIN, uuid } from "../_common.js";

// ── Shared bits ────────────────────────────────────────────────────

export const memberRole = z.enum(["Founder", "Operator", "Employee"]);
/** V1 invite restricts to Founder + Operator. Employee = WhatsApp-only (V1.x). */
export const invitableRole = z.enum(["Founder", "Operator"]);

export const memberRow = z.object({
  membershipId: uuid,
  userId: uuid,
  /** Legacy coarse band (Founder/Operator/Employee). Kept in sync with the
   *  granular role on every assignment; the fallback for un-migrated members. */
  role: memberRole,
  /** Granular role (ADR-0030). Null for memberships that predate role
   *  assignment — those still display via the legacy `role` band. */
  roleId: uuid.nullable(),
  roleName: z.string().nullable(),
  createdAt: z.string(),
  user: z.object({
    name: z.string(),
    phone: phoneIN,
    email: z.string().nullable(),
    avatarUrl: z.string().url().nullable(),
    lastSeenAt: z.string().nullable(),
  }),
});

export const pendingInviteRow = z.object({
  inviteId: uuid,
  phone: phoneIN,
  name: z.string().nullable(),
  /** Legacy coarse band, kept in sync with the granular role. */
  role: memberRole,
  /** Granular role (ADR-0030). Null for legacy band-only invites. */
  roleId: uuid.nullable(),
  roleName: z.string().nullable(),
  invitedAt: z.string(),
  expiresAt: z.string(),
  isExpired: z.boolean(),
});

// ── list ───────────────────────────────────────────────────────────

export const listInput = z.object({});

export const listOutput = z.object({
  members: z.array(memberRow),
  pendingInvites: z.array(pendingInviteRow),
});

// ── invite ─────────────────────────────────────────────────────────

export const inviteInput = z.object({
  phone: phoneIN,
  name: z.string().trim().min(1).max(255).optional(),
  /** Granular role id from `roles.list` (ADR-0030). The server derives the
   *  legacy band from the role's permissions. */
  roleId: uuid,
});

export const inviteOutput = z.object({
  inviteId: uuid,
  /** The same WhatsApp message the invitee receives — surfaced so the
   *  Founder UI can show a "Copy invite link" affordance for sharing
   *  out-of-band (slack, manual copy/paste). */
  joinUrl: z.string().url(),
  phone: phoneIN,
  /** Legacy coarse band derived from the chosen role. */
  role: memberRole,
  /** Display name of the granular role the invitee will receive. */
  roleName: z.string(),
  expiresAt: z.string(),
});

// ── resendInvite ───────────────────────────────────────────────────

export const resendInviteInput = z.object({ inviteId: uuid });
export const resendInviteOutput = z.object({
  inviteId: uuid,
  joinUrl: z.string().url(),
  expiresAt: z.string(),
});

// ── cancelInvite ───────────────────────────────────────────────────

export const cancelInviteInput = z.object({ inviteId: uuid });
export const cancelInviteOutput = z.object({
  inviteId: uuid,
  outcome: z.enum(["cancelled", "already-resolved"]),
});

// ── changeRole ─────────────────────────────────────────────────────

export const changeRoleInput = z.object({
  membershipId: uuid,
  newRole: memberRole,
});
export const changeRoleOutput = z.object({
  membershipId: uuid,
  previousRole: memberRole,
  newRole: memberRole,
});

// ── revokeMembership ───────────────────────────────────────────────

export const revokeMembershipInput = z.object({ membershipId: uuid });
export const revokeMembershipOutput = z.object({
  membershipId: uuid,
  revokedAt: z.string(),
});

// ── lookupInvite (public) ──────────────────────────────────────────

export const lookupInviteInput = z.object({
  token: z.string().min(32).max(64),
});

export const lookupInviteOutput = z.object({
  inviteId: uuid,
  /** Inviting workspace id — used by the join page to detect the
   *  "already-signed-in here" branch without an extra round-trip. */
  companyId: uuid,
  workspaceName: z.string(),
  inviterName: z.string(),
  role: memberRole,
  /** Granular role name (ADR-0030). Null for legacy band-only invites. */
  roleName: z.string().nullable(),
  /** `+91 98xxxx XX42` — first 2 + last 2 digits, rest masked. */
  maskedPhone: z.string(),
  expiresAt: z.string(),
  status: z.enum(["pending", "accepted", "cancelled", "expired"]),
});

// ── acceptInvite (authed, post-OTP) ────────────────────────────────

export const acceptInviteInput = z.object({
  token: z.string().min(32).max(64),
});

export const acceptInviteOutput = z.object({
  companyId: uuid,
  membershipId: uuid,
  role: memberRole,
});

// ── listSessions ───────────────────────────────────────────────────

export const listSessionsInput = z.object({});

export const sessionRow = z.object({
  sessionId: uuid,
  userId: uuid,
  userName: z.string(),
  userPhone: phoneIN,
  expiresAt: z.string(),
  issuedAt: z.string(),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
});

export const listSessionsOutput = z.object({
  sessions: z.array(sessionRow),
});

// ── revokeSession ──────────────────────────────────────────────────

export const revokeSessionInput = z.object({ sessionId: uuid });
export const revokeSessionOutput = z.object({ sessionId: uuid });

// ── revokeAllSessions ──────────────────────────────────────────────

export const revokeAllSessionsInput = z.object({});
export const revokeAllSessionsOutput = z.object({
  revokedCount: z.number().int().nonnegative(),
});
